import * as React from "react";
import { Button } from "@sproutsocial/seeds-react-button";
import type { TypeButtonProps } from "@sproutsocial/seeds-react-button";

/**
 * Props for ModalExternalTrigger component.
 */
export interface ModalExternalTriggerProps
  extends Omit<TypeButtonProps, "onClick"> {
  /** Callback when button is clicked to trigger modal open */
  onTrigger: () => void;
  /** Whether the modal is currently open (for ARIA expanded state) */
  isOpen: boolean;
  /** Optional modal ID for aria-controls attribute */
  modalId?: string;
  /** Optional onClick handler (called before onTrigger) */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * A Button component pre-configured for triggering modals from outside the Modal component tree.
 *
 * ⚠️ **NOT RECOMMENDED** - Prefer using modalTrigger prop or ModalTrigger component.
 * Use this component ONLY as a last resort when architectural constraints prevent keeping
 * the trigger inside the Modal component tree.
 *
 * This component wraps the Seeds Button with automatic ARIA attributes for modal triggers.
 * However, focus restoration still requires manual handling via onCloseAutoFocus callback
 * due to Radix UI's architectural limitations with external triggers.
 *
 * **Why modalTrigger prop is better:**
 * - Automatic ARIA attributes
 * - Automatic focus restoration (no onCloseAutoFocus needed)
 * - Better touch device support
 * - Follows WAI-ARIA Dialog best practices
 *
 * **When to use ModalExternalTrigger:**
 * - Trigger must live outside Modal component tree (e.g., in a page header)
 * - Using Seeds Button as the trigger
 * - Want automatic ARIA attributes without manual hook usage
 *
 * **Usage pattern with focus restoration:**
 * You must still handle focus restoration manually by passing a ref and implementing
 * onCloseAutoFocus on the Modal component.
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const triggerRef = useRef<HTMLButtonElement>(null);
 *
 * return (
 *   <>
 *     <ModalExternalTrigger
 *       ref={triggerRef}
 *       isOpen={isOpen}
 *       onTrigger={() => setIsOpen(true)}
 *       appearance="primary"
 *     >
 *       Open Modal
 *     </ModalExternalTrigger>
 *
 *     <Modal
 *       open={isOpen}
 *       onOpenChange={setIsOpen}
 *       onCloseAutoFocus={(e) => {
 *         e.preventDefault();
 *         triggerRef.current?.focus();
 *       }}
 *     >
 *       <ModalBody>Content</ModalBody>
 *     </Modal>
 *   </>
 * );
 * ```
 *
 * @see useModalExternalTrigger - Hook alternative for non-Button triggers
 */
export const ModalExternalTrigger = React.forwardRef<
  HTMLButtonElement,
  ModalExternalTriggerProps
>(({ isOpen, onTrigger, modalId, onClick, ...buttonProps }, ref) => {
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        onTrigger();
      }
    },
    [onClick, onTrigger]
  );

  return (
    <Button
      ref={ref}
      onClick={handleClick}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-controls={modalId}
      {...buttonProps}
    />
  );
});

ModalExternalTrigger.displayName = "ModalExternalTrigger";
